import * as THREE from 'three'

/**
 * Cross-Section Computer
 * Computes real geometric cross-sections where geometry intersects clipping planes.
 * Based on triangle-plane intersection → contour building → earcut triangulation.
 */

// Cross-section color (Kawasaki green / orange accent)
const CROSS_SECTION_COLOR = 0xff6b35 // Orange accent color from reference

/**
 * Get intersection segments between geometry and a plane
 * Takes a transform matrix to apply to geometry vertices
 */
export function getIntersectionSegments(geometry, plane, clipPlanes, matrix) {
  const segments = []
  const position = geometry.attributes.position
  const index = geometry.index
  
  const v0 = new THREE.Vector3()
  const v1 = new THREE.Vector3()
  const v2 = new THREE.Vector3()
  
  const triangleCount = index ? index.count / 3 : position.count / 3
  
  for (let i = 0; i < triangleCount; i++) {
    if (index) {
      v0.fromBufferAttribute(position, index.getX(i * 3))
      v1.fromBufferAttribute(position, index.getX(i * 3 + 1))
      v2.fromBufferAttribute(position, index.getX(i * 3 + 2))
    } else {
      v0.fromBufferAttribute(position, i * 3)
      v1.fromBufferAttribute(position, i * 3 + 1)
      v2.fromBufferAttribute(position, i * 3 + 2)
    }
    
    // Apply transform matrix to vertices
    if (matrix) {
      v0.applyMatrix4(matrix)
      v1.applyMatrix4(matrix)
      v2.applyMatrix4(matrix)
    }
    
    const d0 = plane.distanceToPoint(v0)
    const d1 = plane.distanceToPoint(v1)
    const d2 = plane.distanceToPoint(v2)
    
    const pts = []
    
    if (d0 * d1 < 0) {
      const t = d0 / (d0 - d1)
      pts.push(new THREE.Vector3().lerpVectors(v0, v1, t))
    }
    if (d1 * d2 < 0) {
      const t = d1 / (d1 - d2)
      pts.push(new THREE.Vector3().lerpVectors(v1, v2, t))
    }
    if (d2 * d0 < 0) {
      const t = d2 / (d2 - d0)
      pts.push(new THREE.Vector3().lerpVectors(v2, v0, t))
    }
    
    if (pts.length === 2) {
      const clipped = clipSegment(pts[0], pts[1], clipPlanes)
      if (clipped) {
        segments.push(clipped)
      }
    }
  }
  
  return segments
}

/**
 * Clip a segment to the clipping planes (keep only parts inside all planes)
 */
function clipSegment(p0, p1, planes) {
  let a = p0.clone()
  let b = p1.clone()
  
  for (const plane of planes) {
    const da = plane.distanceToPoint(a)
    const db = plane.distanceToPoint(b)
    
    if (da < -0.001 && db < -0.001) return null
    if (da >= -0.001 && db >= -0.001) continue
    
    const t = da / (da - db)
    const p = new THREE.Vector3().lerpVectors(a, b, t)
    
    if (da < 0) a = p
    else b = p
  }
  
  if (a.distanceTo(b) < 0.001) return null
  return [a, b]
}

/**
 * Build contours from segments by connecting nearby endpoints
 */
export function buildContours(segments) {
  if (segments.length === 0) return []
  
  const contours = []
  const eps = 0.1
  const remaining = segments.map(s => [s[0].clone(), s[1].clone()])
  
  while (remaining.length > 0) {
    const contour = []
    const first = remaining.shift()
    contour.push(first[0].clone())
    contour.push(first[1].clone())
    
    let maxIterations = remaining.length * 2
    let iterations = 0
    
    while (iterations < maxIterations) {
      iterations++
      let found = false
      const head = contour[0]
      const tail = contour[contour.length - 1]
      
      let bestIdx = -1
      let bestDist = eps
      let bestEnd = null
      let bestPt = null
      
      for (let i = 0; i < remaining.length; i++) {
        const seg = remaining[i]
        const d0t = tail.distanceTo(seg[0])
        const d1t = tail.distanceTo(seg[1])
        const d0h = head.distanceTo(seg[0])
        const d1h = head.distanceTo(seg[1])
        
        if (d0t < bestDist) { bestDist = d0t; bestIdx = i; bestEnd = 'tail'; bestPt = 0 }
        if (d1t < bestDist) { bestDist = d1t; bestIdx = i; bestEnd = 'tail'; bestPt = 1 }
        if (d0h < bestDist) { bestDist = d0h; bestIdx = i; bestEnd = 'head'; bestPt = 0 }
        if (d1h < bestDist) { bestDist = d1h; bestIdx = i; bestEnd = 'head'; bestPt = 1 }
      }
      
      if (bestIdx >= 0) {
        const seg = remaining[bestIdx]
        remaining.splice(bestIdx, 1)
        
        if (bestEnd === 'tail') {
          contour.push(bestPt === 0 ? seg[1].clone() : seg[0].clone())
        } else {
          contour.unshift(bestPt === 0 ? seg[1].clone() : seg[0].clone())
        }
        found = true
      }
      
      if (!found) break
    }
    
    if (contour.length >= 3) {
      contours.push(contour)
    }
  }
  
  return mergeContours(contours, eps * 2)
}

/**
 * Merge contours that share endpoints
 */
function mergeContours(contours, eps) {
  if (contours.length <= 1) return contours
  
  let merged = [...contours]
  let changed = true
  
  while (changed) {
    changed = false
    
    for (let i = 0; i < merged.length && !changed; i++) {
      for (let j = i + 1; j < merged.length && !changed; j++) {
        const c1 = merged[i]
        const c2 = merged[j]
        
        const h1 = c1[0], t1 = c1[c1.length - 1]
        const h2 = c2[0], t2 = c2[c2.length - 1]
        
        let newContour = null
        
        if (t1.distanceTo(h2) < eps) {
          newContour = [...c1, ...c2.slice(1)]
        } else if (t1.distanceTo(t2) < eps) {
          newContour = [...c1, ...c2.slice(0, -1).reverse()]
        } else if (h1.distanceTo(h2) < eps) {
          newContour = [...c1.slice(1).reverse(), ...c2]
        } else if (h1.distanceTo(t2) < eps) {
          newContour = [...c2, ...c1.slice(1)]
        }
        
        if (newContour) {
          merged.splice(j, 1)
          merged[i] = newContour
          changed = true
        }
      }
    }
  }
  
  return merged
}

/**
 * Triangulate a contour into a BufferGeometry using earcut
 */
export function triangulateContour(contour, plane) {
  if (contour.length < 3) return null
  
  const normal = plane.normal.clone()
  let up = new THREE.Vector3(0, 1, 0)
  if (Math.abs(normal.dot(up)) > 0.9) up = new THREE.Vector3(1, 0, 0)
  const right = new THREE.Vector3().crossVectors(up, normal).normalize()
  up = new THREE.Vector3().crossVectors(normal, right).normalize()
  
  const centroid = new THREE.Vector3()
  for (const p of contour) centroid.add(p)
  centroid.divideScalar(contour.length)
  
  const pts2d = contour.map(p => {
    const rel = p.clone().sub(centroid)
    return [rel.dot(right), rel.dot(up)]
  })
  
  const cleaned = [pts2d[0]]
  for (let i = 1; i < pts2d.length; i++) {
    const prev = cleaned[cleaned.length - 1]
    const dist = Math.sqrt((pts2d[i][0] - prev[0])**2 + (pts2d[i][1] - prev[1])**2)
    if (dist > 0.005) {
      cleaned.push(pts2d[i])
    }
  }
  
  if (cleaned.length > 1) {
    const first = cleaned[0]
    const last = cleaned[cleaned.length - 1]
    const dist = Math.sqrt((first[0] - last[0])**2 + (first[1] - last[1])**2)
    if (dist < 0.05) {
      cleaned.pop()
    }
  }
  
  if (cleaned.length < 3) return null
  
  const first3d = contour[0]
  const last3d = contour[contour.length - 1]
  const closeDist = first3d.distanceTo(last3d)
  
  if (closeDist > 0.5 && cleaned.length < 10) {
    return null
  }
  
  const indices = earcut(cleaned.flat(), null, 2)
  
  if (indices.length === 0) return null
  
  const vertices = []
  const normals = []
  
  for (let i = 0; i < indices.length; i += 3) {
    for (let j = 0; j < 3; j++) {
      const idx = indices[i + j]
      if (idx >= cleaned.length) continue
      
      const x2d = cleaned[idx][0]
      const y2d = cleaned[idx][1]
      
      const pos = centroid.clone()
        .add(right.clone().multiplyScalar(x2d))
        .add(up.clone().multiplyScalar(y2d))
      
      vertices.push(pos.x, pos.y, pos.z)
      normals.push(normal.x, normal.y, normal.z)
    }
  }
  
  if (vertices.length < 9) return null
  
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3))
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3))
  
  return geometry
}

// ============================================
// EARCUT TRIANGULATION
// ============================================

function earcut(data, holeIndices, dim) {
  dim = dim || 2
  const hasHoles = holeIndices && holeIndices.length
  const outerLen = hasHoles ? holeIndices[0] * dim : data.length
  let outerNode = linkedList(data, 0, outerLen, dim, true)
  const triangles = []
  if (!outerNode || outerNode.next === outerNode.prev) return triangles
  let minX, minY, maxX, maxY, x, y, invSize
  if (hasHoles) outerNode = eliminateHoles(data, holeIndices, outerNode, dim)
  if (data.length > 80 * dim) {
    minX = maxX = data[0]; minY = maxY = data[1]
    for (let i = dim; i < outerLen; i += dim) {
      x = data[i]; y = data[i + 1]
      if (x < minX) minX = x; if (y < minY) minY = y
      if (x > maxX) maxX = x; if (y > maxY) maxY = y
    }
    invSize = Math.max(maxX - minX, maxY - minY)
    invSize = invSize !== 0 ? 32767 / invSize : 0
  }
  earcutLinked(outerNode, triangles, dim, minX, minY, invSize, 0)
  return triangles
}

function linkedList(data, start, end, dim, clockwise) {
  let last
  if (clockwise === (signedArea(data, start, end, dim) > 0)) {
    for (let i = start; i < end; i += dim) last = insertNode(i / dim, data[i], data[i + 1], last)
  } else {
    for (let i = end - dim; i >= start; i -= dim) last = insertNode(i / dim, data[i], data[i + 1], last)
  }
  if (last && equals(last, last.next)) { removeNode(last); last = last.next }
  if (last) { last.next.prev = last; last.prev.next = last }
  return last
}

function signedArea(data, start, end, dim) {
  let sum = 0
  for (let i = start, j = end - dim; i < end; i += dim) {
    sum += (data[j] - data[i]) * (data[i + 1] + data[j + 1]); j = i
  }
  return sum
}

function insertNode(i, x, y, last) {
  const p = { i, x, y, prev: null, next: null, z: 0, prevZ: null, nextZ: null, steiner: false }
  if (!last) { p.prev = p; p.next = p }
  else { p.next = last.next; p.prev = last; last.next.prev = p; last.next = p }
  return p
}

function removeNode(p) {
  p.next.prev = p.prev; p.prev.next = p.next
  if (p.prevZ) p.prevZ.nextZ = p.nextZ; if (p.nextZ) p.nextZ.prevZ = p.prevZ
}

function equals(p1, p2) { return p1.x === p2.x && p1.y === p2.y }
function eliminateHoles(data, holeIndices, outerNode, dim) { return outerNode }

function earcutLinked(ear, triangles, dim, minX, minY, invSize, pass) {
  if (!ear) return
  if (!pass && invSize) indexCurve(ear, minX, minY, invSize)
  let stop = ear
  while (ear.prev !== ear.next) {
    const prev = ear.prev, next = ear.next
    if (invSize ? isEarHashed(ear, minX, minY, invSize) : isEar(ear)) {
      triangles.push(prev.i, ear.i, next.i)
      removeNode(ear); ear = next.next; stop = next.next; continue
    }
    ear = next
    if (ear === stop) {
      if (!pass) earcutLinked(filterPoints(ear), triangles, dim, minX, minY, invSize, 1)
      else if (pass === 1) { ear = cureLocalIntersections(filterPoints(ear), triangles, dim); earcutLinked(ear, triangles, dim, minX, minY, invSize, 2) }
      else if (pass === 2) splitEarcut(ear, triangles, dim, minX, minY, invSize)
      return
    }
  }
}

function isEar(ear) {
  const a = ear.prev, b = ear, c = ear.next
  if (area(a, b, c) >= 0) return false
  const ax = a.x, bx = b.x, cx = c.x, ay = a.y, by = b.y, cy = c.y
  const x0 = Math.min(ax, bx, cx), y0 = Math.min(ay, by, cy)
  const x1 = Math.max(ax, bx, cx), y1 = Math.max(ay, by, cy)
  let p = c.next
  while (p !== a) {
    if (p.x >= x0 && p.x <= x1 && p.y >= y0 && p.y <= y1 &&
        pointInTriangle(ax, ay, bx, by, cx, cy, p.x, p.y) && area(p.prev, p, p.next) >= 0) return false
    p = p.next
  }
  return true
}

function isEarHashed(ear) { return isEar(ear) }
function indexCurve() {}

function filterPoints(start, end) {
  if (!start) return start; if (!end) end = start
  let p = start, again
  do {
    again = false
    if (!p.steiner && (equals(p, p.next) || area(p.prev, p, p.next) === 0)) {
      removeNode(p); p = end = p.prev; if (p === p.next) break; again = true
    } else p = p.next
  } while (again || p !== end)
  return end
}

function cureLocalIntersections(start, triangles) {
  let p = start
  do {
    const a = p.prev, b = p.next.next
    if (!equals(a, b) && intersects(a, p, p.next, b) && locallyInside(a, b) && locallyInside(b, a)) {
      triangles.push(a.i, p.i, b.i); removeNode(p); removeNode(p.next); p = start = b
    }
    p = p.next
  } while (p !== start)
  return filterPoints(p)
}

function splitEarcut() {}
function area(p, q, r) { return (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y) }

function pointInTriangle(ax, ay, bx, by, cx, cy, px, py) {
  return (cx - px) * (ay - py) >= (ax - px) * (cy - py) &&
         (ax - px) * (by - py) >= (bx - px) * (ay - py) &&
         (bx - px) * (cy - py) >= (cx - px) * (by - py)
}

function intersects(p1, q1, p2, q2) {
  const o1 = sign(area(p1, q1, p2)), o2 = sign(area(p1, q1, q2))
  const o3 = sign(area(p2, q2, p1)), o4 = sign(area(p2, q2, q1))
  return o1 !== o2 && o3 !== o4
}

function sign(num) { return num > 0 ? 1 : num < 0 ? -1 : 0 }

function locallyInside(a, b) {
  return area(a.prev, a, a.next) < 0 ?
    area(a, b, a.next) >= 0 && area(a, a.prev, b) >= 0 :
    area(a, b, a.prev) < 0 || area(a, a.next, b) < 0
}

// ============================================
// MAIN CROSS-SECTION COMPUTATION
// ============================================

/**
 * Compute cross-section meshes for a geometry against clipping planes
 * @param {THREE.BufferGeometry} geometry - The geometry to compute cross-sections for
 * @param {THREE.Plane[]} planes - Array of 6 clipping planes defining the cage
 * @param {THREE.Matrix4} worldMatrix - World transform matrix for the geometry
 * @param {number} color - Color for the cross-section surfaces
 * @returns {THREE.Mesh[]} Array of cross-section meshes
 */
export function computeCrossSections(geometry, planes, worldMatrix, color = CROSS_SECTION_COLOR) {
  const crossSectionMeshes = []
  
  const crossSectionMaterial = new THREE.MeshStandardMaterial({
    color: new THREE.Color(color),
    side: THREE.DoubleSide,
    metalness: 0.2,
    roughness: 0.6,
    emissive: new THREE.Color(color),
    emissiveIntensity: 0.3,
    toneMapped: false
  })
  
  for (let i = 0; i < 6; i++) {
    const plane = planes[i]
    const otherPlanes = planes.filter((_, idx) => idx !== i)
    
    // Get all segments from geometry (transformed by worldMatrix)
    const segments = getIntersectionSegments(geometry, plane, otherPlanes, worldMatrix)
    
    if (segments.length === 0) continue
    
    const contours = buildContours(segments)
    
    for (const contour of contours) {
      const sectionGeometry = triangulateContour(contour, plane)
      if (sectionGeometry) {
        const mesh = new THREE.Mesh(sectionGeometry, crossSectionMaterial.clone())
        mesh.userData.planeIndex = i
        mesh.userData.planeNormal = plane.normal.clone()
        crossSectionMeshes.push(mesh)
      }
    }
  }
  
  return crossSectionMeshes
}

/**
 * Merge multiple geometries and compute cross-sections for the combined result
 */
export function computeCrossSectionsForMeshes(meshes, planes, color = CROSS_SECTION_COLOR) {
  const allMeshes = []
  
  for (const mesh of meshes) {
    if (!mesh.geometry) continue
    
    // Get the world matrix
    mesh.updateMatrixWorld(true)
    const worldMatrix = mesh.matrixWorld.clone()
    
    // Compute cross-sections for this geometry
    const sectionMeshes = computeCrossSections(mesh.geometry, planes, worldMatrix, color)
    allMeshes.push(...sectionMeshes)
  }
  
  return allMeshes
}

/**
 * Update cross-section visibility based on camera position
 * Shows cross-sections only when looking at them from outside
 */
export function updateCrossSectionVisibility(crossSectionMeshes, cameraPosition) {
  for (const mesh of crossSectionMeshes) {
    const planeNormal = mesh.userData.planeNormal
    if (planeNormal) {
      const meshPos = new THREE.Vector3()
      mesh.getWorldPosition(meshPos)
      const toMesh = new THREE.Vector3().subVectors(meshPos, cameraPosition).normalize()
      mesh.visible = planeNormal.dot(toMesh) > 0
    }
  }
}

export { CROSS_SECTION_COLOR }


